import * as React from 'react';
import { useEffect, useState } from 'react';

import { Container, Header } from '@amzn/awsui-components-react-v3/polaris';
import ColumnLayout from '@amzn/awsui-components-react-v3/polaris/column-layout';
import { CopiableText } from 'src/commons/common';
import { createCatalogDetailLink, createDatabaseDetailLink, createSchemaDetailLink } from 'src/routes';
import { ownerLinkItem } from 'src/components/workspaces/common/common';
import ContactInfo from 'src/components/workspaces/common/ContactInfo';
import { SpaceBetween } from '@amzn/awsui-components-react-v3';
import { GetDataSetResponseStructure } from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';
import { generateArnFromId } from 'src/components/utils/arnUtil';
import { getClassification } from 'src/api/classify';

export interface DatasetDetailsContainerProps {
  dataset: GetDataSetResponseStructure;
  option?: any;
  mostRecentOption?: any;
  cti?: string;
  catalogName?: string;
  workspaceNameMap?: any;
}

export const DatasetDetailsContainer = (props: DatasetDetailsContainerProps) => {
  const [classificationStatus, setClassificationStatus] = useState<string>(undefined);

  const statusMap = { ACCEPTED: 'Accepted', OVERRIDDEN: 'Overridden' } as const;

  useEffect(() => {
    if (!props.dataset?.Id) return;

    const fetchStatus = async () => {
      try {
        const response = await getClassification({ datasetId: props.dataset.Id });
        const status = response?.datasetClassification?.reviewStatus;
        setClassificationStatus(statusMap[status as keyof typeof statusMap] || 'Pending Review');
      } catch (error) {
        console.log('Classification status not available:', error);
        setClassificationStatus(undefined);
      }
    };

    fetchStatus();
  }, [props.dataset?.Id]);
  const getDatasetDetailsMetadata = () => {
    const datasetMetadata = [];
    if (props.dataset?.IdInfo?.TableName) {
      datasetMetadata.push(
        <CopiableText name={'Dataset name'} key={'Dataset name'} value={props.dataset?.IdInfo?.TableName} />,
      );
    }

    datasetMetadata.push(
      <CopiableText name={'Dataset title'} key={'Dataset title'} value={props.dataset?.DataSetName} />,
    );
    if (props.dataset?.IdInfo?.CatalogId) {
      datasetMetadata.push(
        <CopiableText
          name={'Catalog ID'}
          key={'Catalog ID'}
          value={props.dataset?.IdInfo?.CatalogId}
          url={createCatalogDetailLink(
            props.dataset?.IdInfo?.DataSourceId,
            props.dataset?.IdInfo?.CatalogId,
            props.dataset?.IdInfo?.ClusterIdentifier,
            props.dataset?.IdInfo?.RedshiftWorkgroupName,
            props.dataset?.IdInfo?.BucketName,
            props.dataset?.IdInfo?.Region,
          )}
        />,
      );
    }

    if (props.dataset?.Owners) {
      datasetMetadata.push(
        <CopiableText
          copiable={false}
          name={'Owner'}
          key={'Owner'}
          value={props.dataset?.Owners.map((owner) => ownerLinkItem(owner, props.workspaceNameMap))}
        />,
      );
    }
    // include dataset description without condition.
    // Users can view a blank one and update if needed.
    datasetMetadata.push(
      <CopiableText name={'Dataset description'} key={'Dataset description'} value={props.dataset?.DataSetDesc} />,
    );
    // Add classification status
    if (classificationStatus !== undefined) {
      datasetMetadata.push(
        <CopiableText name={'Classification status'} key={'Classification status'} value={classificationStatus} />,
      );
    }
    if (props.dataset?.IdInfo?.DatabaseName) {
      datasetMetadata.push(
        <CopiableText
          name={'Database name'}
          key={'DatabaseName'}
          value={props.dataset?.IdInfo?.DatabaseName}
          url={createDatabaseDetailLink(
            props.dataset?.IdInfo?.DataSourceId,
            props.dataset?.IdInfo?.CatalogId,
            props.dataset?.IdInfo?.ClusterIdentifier,
            props.dataset?.IdInfo?.RedshiftWorkgroupName,
            props.dataset?.IdInfo?.BucketName,
            props.dataset?.IdInfo?.DatabaseName,
            props.dataset?.IdInfo?.Region,
          )}
        />,
      );
    }

    if (props.dataset?.IdInfo?.SchemaName) {
      datasetMetadata.push(
        <CopiableText
          name={'Schema name'}
          key={'SchemaName'}
          value={props.dataset?.IdInfo?.SchemaName}
          url={createSchemaDetailLink(
            props.dataset?.IdInfo?.DataSourceId,
            props.dataset?.IdInfo?.CatalogId,
            props.dataset?.IdInfo?.ClusterIdentifier,
            props.dataset?.IdInfo?.RedshiftWorkgroupName,
            props.dataset?.IdInfo?.DatabaseName,
            props.dataset?.IdInfo?.SchemaName,
            props.dataset?.IdInfo?.Region,
          )}
        />,
      );
    }

    if (props.dataset?.IdInfo?.ClusterIdentifier) {
      datasetMetadata.push(
        <CopiableText name={'Cluster name'} key={'ClusterName'} value={props.dataset?.IdInfo?.ClusterIdentifier} />,
      );
    }

    if (props.dataset?.IdInfo?.RedshiftWorkgroupName) {
      datasetMetadata.push(
        <CopiableText
          name={'Workgroup name'}
          key={'RedshiftWorkgroupName'}
          value={props.dataset?.IdInfo?.RedshiftWorkgroupName}
        />,
      );
    }

    if (props.dataset?.IdInfo?.Region) {
      datasetMetadata.push(<CopiableText name={'Region'} key={'Region'} value={props.dataset?.IdInfo?.Region} />);
    }
    if (props.dataset?.Id) {
      datasetMetadata.push(<CopiableText name={'ID'} key={'ID'} value={props.dataset?.Id} />);
    }
    if (props.catalogName) {
      datasetMetadata.push(
        <CopiableText
          name={'Catalog name'}
          key={'CatalogName'}
          value={props.catalogName}
          url={createCatalogDetailLink(
            props.dataset?.IdInfo?.DataSourceId,
            props.dataset?.IdInfo?.CatalogId,
            props.dataset?.IdInfo?.ClusterIdentifier,
            props.dataset?.IdInfo?.RedshiftWorkgroupName,
            props.dataset?.IdInfo?.BucketName,
            props.dataset?.IdInfo?.Region,
          )}
        />,
      );
    }
    if (props.dataset?.CreatedBy) {
      datasetMetadata.push(<CopiableText name={'Created by'} key={'CreatedBy'} value={props.dataset?.CreatedBy} />);
    }
    if (props.dataset?.CreatedOn) {
      datasetMetadata.push(<CopiableText name={'Created on'} key={'CreatedOn'} value={props.dataset?.CreatedOn} />);
    }
    if (props.dataset?.WheeljackConfidenceFileLocation && props.dataset.WheeljackConfidenceFileLocation.trim()) {
      datasetMetadata.push(
        <CopiableText
          name={'Confidence files'}
          key={'Confidence files'}
          value={props.dataset?.WheeljackConfidenceFileLocation}
        />,
      );
    }

    if (props.mostRecentOption?.item || props.option?.item) {
      let version = props.mostRecentOption?.item || props.option?.item;

      if (!!props.cti) {
        datasetMetadata.push(<CopiableText key={'Owner CTI'} name={'Owner CTI'} value={props.cti} />);
      }

      if (version?.Location) {
        datasetMetadata.push(<CopiableText key='S3 location' name='S3 location' value={version?.Location} />);
      }

      if (version?.AdditionalLocations) {
        datasetMetadata.push(
          <CopiableText
            key='Additional locations'
            name='Additional locations'
            value={version.AdditionalLocations.join(', ')}
          />,
        );
      }

      if (version?.SourceUpdateTime) {
        datasetMetadata.push(
          <CopiableText key='Update time' name='Update time' value={version?.SourceUpdateTime?.toString()} />,
        );
      }
    }

    return datasetMetadata;
  };

  return (
    <SpaceBetween size={'m'} direction={'vertical'}>
      <Container header={<Header>Dataset details</Header>}>
        <ColumnLayout columns={3} borders='horizontal'>
          {getDatasetDetailsMetadata()}
        </ColumnLayout>
      </Container>
      {props.dataset && <ContactInfo resource={generateArnFromId(props.dataset?.Id)} />}
    </SpaceBetween>
  );
};
